import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from '..';
import { categoryRequestsApi } from '../../api';
import {
  ICategoryRequest,
  ICategoryRequestListParams,
  ICategoryRequestPagination,
} from '../../types';

interface ICategoryRequestsState {
  loading: boolean;
  actionLoading: boolean;
  requests: ICategoryRequest[];
  pagination: ICategoryRequestPagination;
  selected: ICategoryRequest | null;
}

const initialPagination: ICategoryRequestPagination = {
  hasNext: false,
  currentPage: 1,
  totalPages: 1,
  totalRecords: 0,
};

const initialState: ICategoryRequestsState = {
  loading: false,
  actionLoading: false,
  requests: [],
  pagination: initialPagination,
  selected: null,
};

export const categoryRequestsSlice = createSlice({
  name: 'categoryRequests',
  initialState,
  reducers: {
    setSelectedRequest: (state, { payload }: { payload: ICategoryRequest | null }) => {
      state.selected = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listCategoryRequests.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(listCategoryRequests.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.requests = payload.data;
      state.pagination = payload.pagination;
    });
    builder.addCase(listCategoryRequests.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(approveCategoryRequest.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(approveCategoryRequest.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      state.selected = null;
      state.requests = state.requests.filter((r) => r.id !== payload.id);
      toast.success('Category request approved.');
    });
    builder.addCase(approveCategoryRequest.rejected, (state) => {
      state.actionLoading = false;
    });

    builder.addCase(declineCategoryRequest.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(declineCategoryRequest.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      state.selected = null;
      state.requests = state.requests.filter((r) => r.id !== payload.id);
      toast.success('Category request declined.');
    });
    builder.addCase(declineCategoryRequest.rejected, (state) => {
      state.actionLoading = false;
    });
  },
});

const listCategoryRequests = createAsyncThunk(
  'categoryRequests/list',
  async (params: ICategoryRequestListParams) => {
    const { data } = await categoryRequestsApi.list(params);
    return data;
  }
);

const approveCategoryRequest = createAsyncThunk(
  'categoryRequests/approve',
  async (params: { id: number; cb?: () => void }) => {
    const { id, cb } = params;
    const { data } = await categoryRequestsApi.approve(id);
    cb && cb();
    return (data.data ?? (data as unknown as ICategoryRequest)) as ICategoryRequest;
  }
);

const declineCategoryRequest = createAsyncThunk(
  'categoryRequests/decline',
  async (params: { id: number; reason: string; cb?: () => void }) => {
    const { id, reason, cb } = params;
    const { data } = await categoryRequestsApi.decline(id, reason);
    cb && cb();
    return (data.data ?? (data as unknown as ICategoryRequest)) as ICategoryRequest;
  }
);

export const categoryRequestsSelector = (state: RootState) =>
  state.categoryRequests;
export const { setSelectedRequest } = categoryRequestsSlice.actions;
export {
  listCategoryRequests,
  approveCategoryRequest,
  declineCategoryRequest,
};

export default categoryRequestsSlice.reducer;
